import { Expression } from "@/components/Expressions/types";
import BubbleInvitationEditor from "./BubbleInvitationEditor";
import PopoverInvitationEditor from "./PopoverInvitationEditor";
import BannerInvitationEditor from "./BannerInvitationEditor";
import InlineInvitationEditor from "./InlineInvitationEditor";
import React from "react";

export interface Invitation {
  id?: number;
  siteId: number;
  type: "bubble" | "popover" | "banner" | "inline";
  name: string;
  description: string;
  resetFrequency: InvitationResetFrequency;
  triggerExpression: Expression;
  routeToTeamId?: number;

  // V2 display behaviour
  maxFires?: number;
  allowDuringChat?: boolean;
  floatOntoScreen?: boolean;
  startingAnimation?: InvitationAnimation;

  // V2 custom skin
  htmlContent?: string;
  cssContent?: string;
}

export const InvitationResetFrequencies = [
  "Never",
  "Hourly",
  "Daily",
  "Weekly",
  "Monthly",
  "Yearly"
] as const;

export type InvitationResetFrequency =
  (typeof InvitationResetFrequencies)[number];

export const InvitationAnimations = [
  "right-center",
  "left-center",
  "bottom-center",
  "top-center"
] as const;
export type InvitationAnimation = (typeof InvitationAnimations)[number];

export interface BubbleInvitation extends Invitation {
  type: "bubble";
  messages: string[];
  messageDelaySeconds: number;
}

export interface PopoverInvitation extends Invitation {
  type: "popover";
  messages: string[];
  messageDelaySeconds: number;
  agentAvatarUserId?: number;
  ctaLabel: string;
}

export interface BannerInvitation extends Invitation {
  type: "banner";
  message: string;
  position: "top" | "bottom";
  ctaLabel: string;
}

export interface InlineInvitation extends Invitation {
  type: "inline";
  contentType: "inline-chat" | "custom-html";
  placeholderText?: string;
  ctaLabel?: string;
}

export const invitationTypeDefs = [
  {
    label: "Bubble",
    value: "bubble",
    description: "A classic chat bubble that animates with one or more messages — great for proactive engagement.",
    editor: ({
      invitation,
      errors,
      onUpdate
    }: {
      invitation: Invitation;
      errors: Record<string, string>;
      onUpdate(invitation: BubbleInvitation): void;
    }) => {
      return (
        <BubbleInvitationEditor
          invitation={invitation as BubbleInvitation}
          errors={errors}
          onUpdate={onUpdate}
        />
      );
    }
  },
  {
    label: "Popover",
    value: "popover",
    description: "A floating card with agent avatar and a CTA button — great for proactive engagement on high-intent pages.",
    editor: ({
      invitation,
      errors,
      onUpdate
    }: {
      invitation: Invitation;
      errors: Record<string, string>;
      onUpdate(invitation: PopoverInvitation): void;
    }) => {
      return (
        <PopoverInvitationEditor
          invitation={invitation as PopoverInvitation}
          errors={errors}
          onUpdate={onUpdate}
        />
      );
    }
  },
  {
    label: "Banner",
    value: "banner",
    description: "A slim sticky bar at the top or bottom of the page — non-intrusive, high visibility.",
    editor: ({
      invitation,
      errors,
      onUpdate
    }: {
      invitation: Invitation;
      errors: Record<string, string>;
      onUpdate(invitation: BannerInvitation): void;
    }) => {
      return (
        <BannerInvitationEditor
          invitation={invitation as BannerInvitation}
          errors={errors}
          onUpdate={onUpdate}
        />
      );
    }
  },
  {
    label: "Inline",
    value: "inline",
    description: "An embedded widget inside the page — inline chat input or fully custom HTML skin.",
    editor: ({
      invitation,
      errors,
      onUpdate
    }: {
      invitation: Invitation;
      errors: Record<string, string>;
      onUpdate(invitation: InlineInvitation): void;
    }) => {
      return (
        <InlineInvitationEditor
          invitation={invitation as InlineInvitation}
          errors={errors}
          onUpdate={onUpdate}
        />
      );
    }
  }
];
